import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';

import BooleanBox from '../../../../components/boolean-box';
import TextInput from '../../../../components/text-input';
import { validate } from '../../../../utils/validation';

import { userAccessSchema } from './user-access-schema';

const newUser = [
    {
        id: 'new',
        email: '',
        read: 1,
        write: 0,
        admin: 0,
    },
];

// A single access row. The existing-user and new-user rows shared the same
// email/read/write/admin cells and differed only in the first cell and the
// trailing action button, so those are passed in.
const UserAccessRow = ({ user, firstCell, action, renderText, renderBool }) => (
    <tr>
        <td>{firstCell}</td>
        <td>{renderText(user, 'email')}</td>
        <td>{renderBool(user, 'read')}</td>
        <td>{renderBool(user, 'write')}</td>
        <td>{renderBool(user, 'admin')}</td>
        <td>{action}</td>
    </tr>
);

UserAccessRow.propTypes = {
    user: PropTypes.object,
    firstCell: PropTypes.node,
    action: PropTypes.node,
    renderText: PropTypes.func,
    renderBool: PropTypes.func,
};

class UserAccess extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            accountAccess: this.props.adminReducer.accountAccess,
            saveButton: {},
            newUser,
            errors: {},
        };

        if (!this.state.accountAccess.length) {
            props.fetchAccountAccess();
        }

        this.handleSave = R.bind(this.handleSave, this);
        this.handleAdd = R.bind(this.handleAdd, this);
        this.handlePermission = R.bind(this.handlePermission, this);
        this.handleEmail = R.bind(this.handleEmail, this);
        this.QuickBool = R.bind(this.QuickBool, this);
        this.QuickText = R.bind(this.QuickText, this);
    }

    componentDidUpdate({ adminReducer }) {
        if (adminReducer.accountAccess !== this.props.adminReducer.accountAccess) {
            this.setState({
                accountAccess: this.props.adminReducer.accountAccess,
                newUser,
                errors: {},
            });
        }
    }

    QuickBool(user, field) {
        return (
            <BooleanBox
                id={String(user.id)}
                field={user.id + '-' + field}
                checked={!!user[field]}
                onChange={this.handlePermission}
            />
        );
    }

    QuickText(user, field) {
        const placeHolders = {
            account_id: 'SANS Account ID',
            email: 'E Mail',
        };

        return (
            <TextInput
                id={user.id + '-' + field}
                value={user[field]}
                placeholder={placeHolders[field]}
                error={field === 'email' ? this.state.errors[user.id] : null}
                onChange={this.handleEmail}
            />
        );
    }

    handlePermission({ target }) {
        const [id, field] = R.split('-', target.id);
        const listKey = id === 'new' ? 'newUser' : 'accountAccess';
        const checked = target.checked;

        this.setState((prev) => ({
            saveButton: R.assoc(id, true, prev.saveButton),
            [listKey]: R.map((user) => {
                if (String(user.id) === id) {
                    //Make sure privilages prior privilages are (un)selected
                    if (checked) {
                        switch (field) {
                            case 'admin':
                                user.admin = 1;
                            //falls through
                            case 'write':
                                user.write = 1;
                            //falls through
                            case 'read':
                            default:
                                user.read = 1;
                        }
                    } else {
                        switch (field) {
                            case 'read':
                                user.read = 0;
                            //falls through
                            case 'write':
                                user.write = 0;
                            //falls through
                            case 'admin':
                            default:
                                user.admin = 0;
                        }
                    }
                    return R.assoc(field, checked ? 1 : 0, user);
                }
                return user;
            }, prev[listKey]),
        }));
    }

    handleEmail({ target }) {
        const [id, field] = R.split('-', target.id);
        const listKey = id === 'new' ? 'newUser' : 'accountAccess';
        const value = target.value;

        this.setState((prev) => ({
            saveButton: R.assoc(id, true, prev.saveButton),
            [listKey]: R.map((user) => {
                if (String(user.id) === id) {
                    return R.assoc(field, value, user);
                }
                return user;
            }, prev[listKey]),
            errors:
                field === 'email' && prev.errors[id]
                    ? R.assoc(id, validate(userAccessSchema, { email: value }).errors.email, prev.errors)
                    : prev.errors,
        }));
    }

    handleAdd() {
        const { valid, errors } = validate(userAccessSchema, { email: this.state.newUser[0].email });

        if (!valid) {
            this.setState((prev) => ({ errors: R.assoc('new', errors.email, prev.errors) }));
            return;
        }

        this.setState((prev) => ({ errors: R.dissoc('new', prev.errors) }));
        this.props.createAccountAccess(this.state.newUser[0]);
    }

    handleSave({ target }) {
        const dataId = target.dataset.id;
        const data = R.find(R.propEq('id', Number.parseInt(dataId, 10)), this.state.accountAccess);

        const { valid, errors } = validate(userAccessSchema, { email: data.email });

        if (!valid) {
            this.setState((prev) => ({ errors: R.assoc(dataId, errors.email, prev.errors) }));
            return;
        }

        this.setState((prev) => ({
            saveButton: R.assoc(dataId, false, prev.saveButton),
            errors: R.dissoc(dataId, prev.errors),
        }));

        this.props.setAccountAccess(data);
    }

    render() {
        return (
            <div>
                <h2>User Access</h2>
                <Table>
                    <thead>
                        <tr>
                            <th>Id</th>
                            <th>Email (for readability)</th>
                            <th>Read</th>
                            <th>Write</th>
                            <th>Admin</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {R.map(
                            (user) => (
                                <UserAccessRow
                                    key={user.id}
                                    user={user}
                                    firstCell={user.account_id}
                                    renderText={this.QuickText}
                                    renderBool={this.QuickBool}
                                    action={
                                        <Button
                                            data-id={user.id}
                                            disabled={!this.state.saveButton[user.id]}
                                            onClick={this.handleSave}
                                        >
                                            Save
                                        </Button>
                                    }
                                />
                            ),
                            this.state.accountAccess,
                        )}
                        {R.map(
                            (user) => (
                                <UserAccessRow
                                    key={user.id}
                                    user={user}
                                    firstCell={this.QuickText(user, 'account_id')}
                                    renderText={this.QuickText}
                                    renderBool={this.QuickBool}
                                    action={<Button onClick={this.handleAdd}>Add</Button>}
                                />
                            ),
                            this.state.newUser,
                        )}
                    </tbody>
                </Table>
            </div>
        );
    }
}

UserAccess.propTypes = {
    adminReducer: PropTypes.object,
    fetchAccountAccess: PropTypes.func,
    createAccountAccess: PropTypes.func,
    setAccountAccess: PropTypes.func,
};

export default UserAccess;
